import {
  ChangeEvent,
  createContext,
  HTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  MouseEvent as ReactMouseEvent,
} from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { MediaConfigScope, MediaField, MediaType } from "../types"
import { css, cx } from "@emotion/css"
import {
  fetchMediaFields,
  patchField,
  patchMediaType,
} from "api/globalMediaTypes"
import { queryCache, useMutation, useQuery } from "react-query"

import Button from "ui/buttons/Button"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import { Loading } from "components/Loading"
import { MultiSelect } from "../../components/MultiSelect"
import { Paper } from "@material-ui/core"
import SimpleTable from "../../components/SimpleTable"
import { WithAuthority } from "components/WithAuthority"
import { assoc } from "lodash/fp"
import get from "lodash/fp/get"
import { useI18n } from "providers/I18n"

type Props = {
  mediaType: MediaType
  configOwnerId?: string
  configScope?: string
}

export default function Fields(props: Props) {
  const { t } = useI18n()
  const { mediaType, configScope = "GLOBAL", configOwnerId } = props
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const [value, setValue] = useState<MediaField[]>(mediaType.mediaFields || [])
  const [state, setState] = useState<MediaField[]>(mediaType.mediaFields || [])
  const [open, setOpen] = useState<boolean>(false)
  const [errors, setErrors] = useState<ErrorsMap | undefined>({})
  const [patchErrors, setPatchErrors] = useState<ErrorsMap | undefined>({})

  // ** fetch fields
  const { data, status } = useQuery(
    [
      "global-media-fields",
      { configScope: configScope as MediaConfigScope, configOwnerId },
    ],
    fetchMediaFields,
  )
  const isLoading = status === "loading"

  // ** patch media type field ids
  const [editMediaType] = useMutation(patchMediaType, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("global-media-types")
      setErrors({})
      handleSelectClose()
    },
    onError: (e: ApiError, variables: Partial<MediaType>) => {
      const error = e as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
      setModalInfo(undefined)
    },
  })

  // ** patch field inputs
  const [editField] = useMutation(patchField, {
    onSuccess: () => queryCache.invalidateQueries("global-media-fields"),
    onError: (e: ApiError, variables: Partial<MediaField> & { id: string }) => {
      const error = e as ApiError
      const errorMap = error?.errorsMap ?? {}
      setPatchErrors(() => ({
        ...errorMap,
        id: variables.id,
      }))
    },
  })
  const handleChange = (
    evt: ChangeEvent<
      | HTMLInputElement
      | HTMLTextAreaElement
      | {
          name?: string | undefined
          value: unknown
        }
    >,
  ) => {
    const { name, value } = evt.target
    !!name && setState((s) => assoc(name, value, s))
  }

  const onChange = (
    evt: ChangeEvent<
      | HTMLInputElement
      | HTMLTextAreaElement
      | {
          name?: string | undefined
          value: unknown
        }
    >,
  ) => {
    !!handleChange && handleChange(evt)
    setPatchErrors(undefined)
  }

  const mediaFieldIds = useMemo(
    () =>
      !!mediaType?.mediaFields
        ? mediaType?.mediaFields?.map((mf) => mf.id)
        : [],
    [mediaType?.mediaFields],
  )

  const handleRemove = useCallback(
    (id: string) => {
      const updatedMediaFieldIds = mediaFieldIds.length
        ? mediaFieldIds.filter((mf: string) => mf !== id)
        : []
      setModalInfo({
        title: t("admin.mediaType.fields.remove.title"),
        type: "primary_destructive",
        description: t("admin.mediaType.fields.remove.description"),
        submitLabel: t("admin.mediaType.fields.remove.action"),
        onSubmit: async () =>
          await editMediaType({
            id: mediaType?.id,
            mediaFieldIds: updatedMediaFieldIds,
          }),
      })
    },
    [editMediaType, mediaType?.id, mediaFieldIds, t],
  )

  const onUpdateMediaType = useCallback(
    async (mediaFields) => {
      editMediaType({ id: mediaType?.id, mediaFieldIds: mediaFields })
    },
    [editMediaType, mediaType?.id],
  )

  const onUpdateField = useCallback(
    async (name, cellName, value, id) => {
      value !== get(cellName, mediaType.mediaFields) &&
        editField({ id, [name]: value })
    },
    [editField, mediaType.mediaFields],
  )

  const handleSelectOpen = () => setOpen(true)

  const handleSelectClose = () => setOpen(false)

  const handleAdd = useCallback(
    (e: ReactMouseEvent<HTMLButtonElement> | undefined) => {
      e?.preventDefault()
      const mediaFieldIds = value?.map((s) => s?.id)
      onUpdateMediaType(mediaFieldIds)
    },
    [onUpdateMediaType, value],
  )

  const handleBlur = useCallback(() => {
    setValue(mediaType.mediaFields || [])
    handleSelectClose()
    setErrors(undefined)
  }, [mediaType.mediaFields])

  const handleClose = useCallback(() => {
    setValue(mediaType.mediaFields || [])
    handleSelectClose()
    setErrors(undefined)
  }, [mediaType.mediaFields])

  const isModalOpen = Boolean(modalInfo)

  const handleToggleOption = (selectedOptions: MediaField[]) =>
    setValue(selectedOptions)

  const handleClearOptions = () => setValue([])

  const errorMsgInDropdown = t(Object.values(errors ?? [])[0])

  const context = useMemo(
    () => ({
      value,
      handleAdd,
      handleClose,
      errorMsg: errorMsgInDropdown,
    }),
    [value, handleAdd, handleClose, errorMsgInDropdown],
  )

  useEffect(() => {
    setValue(() => mediaType.mediaFields)
  }, [mediaType.mediaFields])

  return (
    <contextProps.Provider value={context}>
      <div className={styles.inputContainer}>
        <WithAuthority allowed={["AUTH_EDIT_MEDIA_TYPE"]}>
          <MultiSelect
            label={t("admin.mediaFields.field.add.label")}
            placeholder={t("admin.mediaFields.field.add.placeholder")}
            items={data || []}
            getOptionLabel={(option: Record<string, string>) => option.name}
            selectedValues={value}
            renderOption={(option: Record<string, string>) => (
              <>
                <span className={styles.option}>{option?.semanticName}</span>{" "}
                <span className={styles.option}>{option?.name}</span>
              </>
            )}
            onToggleOption={handleToggleOption}
            onClearOptions={handleClearOptions}
            paperComponent={PaperComponent}
            onOpen={handleSelectOpen}
            onBlur={handleBlur}
            open={open}
            onClose={handleSelectClose}
          />
        </WithAuthority>
      </div>
      {isLoading ? (
        <Loading />
      ) : (
        <SimpleTable
          handleDelete={handleRemove}
          data={state}
          onUpdate={onUpdateField}
          onChange={onChange}
          errors={patchErrors}
        />
      )}
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
    </contextProps.Provider>
  )
}

const PaperComponent = (props: HTMLAttributes<HTMLElement>) => {
  const { t } = useI18n()
  const { children } = props
  const { value, handleAdd, handleClose, errorMsg } = useContextProps()
  return (
    <Paper>
      <div className={cx("BodyBody-2Dark-greyRegular", styles.header)}>
        {t("admin.mediaTypes.semantic")}
        <div>{t("admin.mediaTypes.label")}</div>
      </div>
      {children}
      <>
        <span className={cx("BodyBody-2RedRegular", styles.errorMsg)}>
          {errorMsg}
        </span>
        <ButtonGroup className={styles.buttonGroup}>
          <Button variant="outlined" color="primary" onMouseDown={handleClose}>
            {t("modal.label.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onMouseDown={handleAdd}
            disabled={!value.length}
          >
            {t("button.add")}
          </Button>
        </ButtonGroup>
      </>
    </Paper>
  )
}

const contextProps = createContext<
  | {
      value: MediaField[]
      handleAdd: (e: ReactMouseEvent<HTMLButtonElement> | undefined) => void
      handleClose: VoidFunction
      errorMsg: string
    }
  | undefined
>(undefined)

export const useContextProps = () => {
  const paperProps = useContext(contextProps)

  if (paperProps === undefined) {
    throw new Error("error")
  }
  return paperProps
}

const styles = {
  inputContainer: css`
    display: flex;
    align-items: center;
    margin-top: var(--spacing-xxl-3);
  `,
  button: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 1;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-hover: transparent;
    --btn-bg-active: transparent;
    --btn-border-color-hover: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;
    &:focus {
      box-shadow: none;
    }
    margin-left: var(--spacing-xxl-3);
  `,
  option: css`
    width: 200px;
    margin: 0 4px;
  `,
  buttonGroup: css`
    text-align: right;
    padding: var(--spacing-m);
    background-color: var(--lighter-grey);
  `,
  errorMsg: css`
    padding-left: var(--spacing-m);
  `,
  header: css`
    display: flex;
    grid-gap: var(--spacing-xxl-7);
    padding-top: var(--spacing-m);
    margin-bottom: var(--spacing-m);
    margin-left: var(--spacing-xxl-6);
  `,
}
